// lib
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { collator } from '@aytm/app/stats/lib/helpers'

import { WEIGHTING_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'

// components
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { InputSwitch } from 'primereact/inputswitch'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

import containerExpander from '@aytm/lib/layout/container-expander'

// import WeightingList from '@aytm/app/stats/page/sidebar/weighting/WeightingList'

// styles
import style from '@aytm/app/stats/page/sidebar/weighting/styles/WeightingPanel.module.scss'

const Weighting = ({ root, weighting, appliedWeighting, surveyData, disabled, onChange, onUpdate, onLoad }) => {
  const [weightingDisabled, setWeightingDisabled] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)

  const componentRef = useRef(null)
  const containerExpanderRef = useRef(null)

  const dropdownsRef = useRef({})

  const weighted = useMemo(
    () =>
      weighting.type === appliedWeighting.type &&
      JSON.stringify(weighting.items) === JSON.stringify(appliedWeighting.items),
    [weighting, appliedWeighting]
  )

  const weightingOptions = useMemo(() => {
    const options = [
      { value: '*', text: 'Choose weighting variable' },
      { value: 'var', text: 'Weight by Custom variable' }
      // { value: 'custom', text: 'Custom / Advanced' }
    ]
    if (0 < surveyData.census.length) {
      return options.splice(1, 0, {
        value: 'census',
        text: `Weight by US Census`
      })
    }
    return options
  }, [surveyData.census])

  const censusOptions = useMemo(
    () =>
      surveyData.census
        .map(value => {
          if (value == undefined) return null
          return {
            value: value.join('+'),
            text: value.join(', ')
          }
        })
        .filter(v => null !== v),
    [surveyData.census]
  )

  const varOptions = useMemo(() => {
    const options = []
    const customVariables = surveyData.customVariables || []
    customVariables.forEach(value => {
      options.push({
        value: value,
        text: value
      })
    })
    if (
      'var' == weighting.type &&
      'var' == appliedWeighting.type &&
      0 < appliedWeighting.items.length &&
      weighting.items[0] == appliedWeighting.items[0] &&
      0 > customVariables.indexOf(appliedWeighting.items[0])
    ) {
      options.push({
        value: appliedWeighting.items[0],
        text: appliedWeighting.items[0]
      })
    }
    options.sort((a, b) => collator.compare(a.text, b.text))
    return options
  }, [weighting, appliedWeighting, surveyData.customVariables])

  const onShowDropdown = useCallback(
    key => {
      if (
        null !== containerExpanderRef.current &&
        null !== dropdownsRef.current &&
        dropdownsRef.current[key] != undefined
      ) {
        containerExpanderRef.current.expand(dropdownsRef.current[key].getOverlay())
      }
    },
    [containerExpanderRef, dropdownsRef]
  )
  const onHideDropdown = useCallback(
    key => {
      if (
        null !== containerExpanderRef.current &&
        null !== dropdownsRef.current &&
        dropdownsRef.current[key] != undefined
      ) {
        containerExpanderRef.current.unexpand(dropdownsRef.current[key].getOverlay())
      }
    },
    [containerExpanderRef, dropdownsRef]
  )

  // const onAddItem = useCallback(() => {
  //   const items = weighting.items.push({ key: '', items: [] })
  //   onChange(weighting.active, weighting.type, items)
  // }, [weighting])
  // const onUpdateItem = useCallback(
  //   (index, value) => {
  //     const items = weighting.items.slice(index, 0, value)
  //     onChange(weighting.active, weighting.type, items)
  //   },
  //   [weighting]
  // )
  // const onRemoveItem = useCallback(
  //   index => {
  //     const items = weighting.items.slice(index, 1)
  //     onChange(weighting.active, weighting.type, items)
  //   },
  //   [weighting]
  // )

  useEffect(() => {
    setIsProcessing(false)
  }, [weighted])

  useEffect(() => {
    if (null !== componentRef.current) {
      containerExpanderRef.current = containerExpander(componentRef.current, { margin: 20 })
    }
  }, [componentRef])

  useEffect(() => {
    if (null !== dropdownsRef.current) {
      delete dropdownsRef.current['census']
      delete dropdownsRef.current['var']
      delete dropdownsRef.current['custom']
    }
  }, [weighting.type])

  useEffect(() => {
    setWeightingDisabled(disabled || '*' == weighting.type || !weighting.items.length)
  }, [disabled, weighting])

  useEffect(() => {
    if (onLoad && 'function' == typeof onLoad) {
      onLoad()
    }
  }, [weighting, onLoad])

  const onSelect = value => {
    switch (value) {
      case 'census':
        onChange(
          weighting.active,
          value,
          ('census' == appliedWeighting.type
            ? appliedWeighting.items
            : 0 < censusOptions.length && censusOptions[0]['value']) || []
        )
        break
      case 'var':
        onChange(
          weighting.active,
          value,
          ('var' == appliedWeighting.type
            ? appliedWeighting.items
            : 0 < varOptions.length && [varOptions[0]['value']]) || []
        )
        break
      case 'custom':
        onChange(weighting.active, value, 'custom' == appliedWeighting.type ? appliedWeighting.items || [] : [])
        break
      default:
        onChange(false, value, [])
        break
    }

    setIsProcessing(false)
  }

  const onCensusSelect = value => {
    onChange(weighting.active, weighting.type, value.split('+'))

    setIsProcessing(false)
  }
  const onVariableSelect = value => {
    onChange(weighting.active, weighting.type, value.split('+'))

    setIsProcessing(false)
  }

  const onApply = () => {
    setIsProcessing(true)
    onUpdate()
  }
  const onCancel = () => {
    onChange(appliedWeighting.active || false, appliedWeighting.type || '*', appliedWeighting.items || [])
  }

  const renderButtons = (type = 'all') =>
    !weighted && appliedWeighting.active && 0 < appliedWeighting.items.length ? (
      <div className={`${style.row} ${style['row-buttons']} p-fluid`}>
        {('all' == type || 'cancel' == type) && (
          <div className="p-field p-col-5">
            <Button
              className="p-button-stat p-button-secondary"
              label="Cancel"
              disabled={isProcessing}
              onClick={isProcessing ? null : onCancel}
            />
          </div>
        )}
        {('all' == type || 'apply' == type) && (
          <div className="p-field p-col-5">
            <Button
              className="p-button-stat"
              label="Apply"
              iconPos="right"
              loading={isProcessing}
              disabled={isProcessing}
              onClick={isProcessing ? null : onApply}
            />
          </div>
        )}
      </div>
    ) : null

  return (
    <div ref={componentRef} className={style.component}>
      <div className={style['component-title']}>
        Weighting
        <SidebarPopup id="weighting" root={root} help={WEIGHTING_HELP} width="300px" />
      </div>

      {!weightingDisabled && (
        <>
          <div className={`${style.row} p-fluid`}>
            <div className="p-field p-col-12">
              <InputSwitch
                id="stat-weighting-switch"
                className="stat-weighting-switch -dtr-stat-weighting-switch p-subtheme-contrast"
                checked={weighting.active}
                disabled={disabled}
                onChange={disabled ? null : event => onUpdate(event.value)}
              />
              <span className={style['row-label']} onClick={disabled ? null : () => onUpdate(!weighting.active)}>
                Weighting is {weighting.active ? 'ON' : 'OFF'}
              </span>
            </div>
          </div>
        </>
      )}
      <div className={`${style.row} p-fluid`}>
        <div className="p-field p-col-12">
          <Dropdown
            data-testid="weighting-type"
            ref={element => (dropdownsRef.current['type'] = element)}
            placeholder="Choose weighting options"
            value={weighting.type}
            options={weightingOptions}
            optionValue="value"
            optionLabel="text"
            fixedDropdownDir="bottom"
            disabled={disabled}
            onChange={disabled ? null : event => onSelect(event.value)}
            onShow={disabled ? null : () => onShowDropdown('type')}
            onHide={disabled ? null : () => onHideDropdown('type')}
          />
        </div>
      </div>
      {'census' == weighting.type && (
        <>
          <div className={`${style.row} p-fluid`}>
            <div className="p-field p-col-12">
              <Dropdown
                data-testid="weighting-census"
                ref={element => (dropdownsRef.current['census'] = element)}
                placeholder="Choose census options"
                value={weighting.items.join('+')}
                options={censusOptions}
                optionValue="value"
                optionLabel="text"
                fixedDropdownDir="bottom"
                disabled={disabled}
                onChange={disabled ? null : event => onCensusSelect(event.value)}
                onShow={disabled ? null : () => onShowDropdown('census')}
                onHide={disabled ? null : () => onHideDropdown('census')}
              />
            </div>
          </div>
          {renderButtons()}
          {appliedWeighting.active && 0 < appliedWeighting.items.length && (
            <div className={`${style.row} p-fluid`}>
              <div className={style.note}>
                <b>
                  Weighted by {appliedWeighting.items.slice(0, -1).join(', ')}
                  {1 < appliedWeighting.items.length ? ' and ' : ''}
                  {appliedWeighting.items[appliedWeighting.items.length - 1]}
                </b>
                <br />
                Maximum Amplification of 1 response: <b>x5</b>
                <br />
                Minimum Effective Sample Size: <b>50%</b>
                <br />
                To exceed these safe settings please&nbsp;
                <a href="/resources/contact-us" target="_blank" rel="noreferrer">
                  contact us
                </a>
                .
              </div>
            </div>
          )}
        </>
      )}
      {'var' == weighting.type &&
        (0 < varOptions.length ? (
          <>
            <div className={`${style.row} p-fluid`}>
              <div className="p-field p-col-12">
                <Dropdown
                  data-testid="weighting-var"
                  ref={element => (dropdownsRef.current['var'] = element)}
                  placeholder="Choose custom variable options"
                  value={weighting.items.join('+')}
                  options={varOptions}
                  optionValue="value"
                  optionLabel="text"
                  fixedDropdownDir="bottom"
                  disabled={disabled}
                  onChange={disabled ? null : event => onVariableSelect(event.value)}
                  onShow={disabled ? null : () => onShowDropdown('var')}
                  onHide={disabled ? null : () => onHideDropdown('var')}
                />
              </div>
            </div>
            {renderButtons()}
            {0 < appliedWeighting.items.length && 0 > surveyData.customVariables.indexOf(appliedWeighting.items[0]) && (
              <div className={`${style.row} p-fluid`}>
                <div className={style.note}>Weighting wasn&apos;t applied</div>
              </div>
            )}
            {weighted && 0 < surveyData.undefs && appliedWeighting.active && 0 < appliedWeighting.items.length && (
              <div className={`${style.row} p-fluid`}>
                <div className={`${style.note} ${style['note-warning']}`}>
                  Warning: some respondents don&apos;t have weights assigned. Weight of 1.0 was assumed for them.
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {renderButtons('cancel')}
            <div className={`${style.row} p-fluid`}>
              <div className={style.note}>
                You don&apos;t seem to have any custom variables. In order to weight by custom variables, please upload
                it first to this Results page.
                <br />
                <a
                  href="https://helpcenter.aytm.com/hc/en-us/articles/360048760532-Custom-Variable-Upload"
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn more &raquo;
                </a>
              </div>
            </div>
          </>
        ))}
      {/* {'custom' == weighting.type && (
        <>
          <WeightingList
            items={weighting.items}
            surveyData={surveyData}
            onAdd={onAddItem}
            onUpdate={onUpdateItem}
            onRemove={onRemoveItem}
          />
          {renderButtons()}
        </>
      )} */}
    </div>
  )
}

Weighting.propTypes = {
  root: PropTypes.any,
  weighting: PropTypes.object.isRequired,
  appliedWeighting: PropTypes.object.isRequired,
  surveyData: PropTypes.object.isRequired,
  undefs: PropTypes.number,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onLoad: PropTypes.func
}

Weighting.displayName = 'Weighting'

export default Weighting
